
import dotProp from 'dot-prop-immutable'

import * as actions from '../actionTypes'
import SelectedDecisionContainer from '../components/counseling-session/selected-decision/SelectedDecisionContainer'
import EducationalMaterialsContainer from '../components/counseling-session/educational-materials/EducationalMaterialsContainer'
import EnterFactorsContainer from '../components/counseling-session/enter-factors/EnterFactorsContainer'
import TopThreeFactorsContainer from '../components/counseling-session/top-three-factors/TopThreeFactorContainer'
import FactorEffectContainer from '../components/counseling-session/factor-effect/FactorEffectContainer'
import OrderOfImportanceContainer from '../components/counseling-session/order-of-importance/OrderOfImportanceContainer'
import ComparingImportanceContainer from '../components/counseling-session/comparing-importance/ComparingImportanceContainer'
import ReportContainer from '../components/counseling-session/report/ReportContainer'
import DemographicsContainer from '../components/counseling-session/demographics/DemographicsContainer'
import CommentsContainer from '../components/counseling-session/comments/CommentsContainer'

export const initialState = {
  /* If user creates or recalls session, don't load based on cookie. If page is refreshed,
     load from cookie session id if available. */
  bypassSessionLoadFromCookie: false,
  counselingSessionData: {
    factorsOption1: [],
    factorsOption2: [],
    topThreeFactors: [],
    topThreeFactorsWithAffectWeights: [],
    topThreeFactorsWithCompareWeights: [],
    // NOTE: these defaults values are DB expected values/types if User has not yet entered information
    address1: "",
    address2: "",
    caseId: "",
    checklistComplete: false,
    city: "",
    counselorCampusKey: "",
    counselorComments: "",
    counselorFirstName: "",
    counselorLastName: "",
    dateOfBirth: "",
    decisionName: "", // NOTE: read-only
    education: "",
    educationMaterials: [], // NOTE: read-only
    email: "",
    ethnicity: "",
    finalWeight: 0.0,
    firstName: "",
    gender: "",
    hasBeenRanked: 0,
    indicatorMessage: "",
    informationReviewed: false,
    isComplete: false,
    lastName: "",
    lastScreenSaved: "",
    maritalStatus: "",
    medicalRecordNumber: "",
    options: [], // NOTE: read-only
    optionsResults: [], // NOTE: read-only
    primaryLanguageId: 1,
    race: "",
    state: "",
    telephone1: "",
    telephone2: "",
    userComments: "",
    zipCode: "",
    primaryLanguageCode: "",
    primaryLanguageName: ""
  },
  errors: {
    pdfGeneration: false,
    sessionSave: false
  },
  editingFactorIndex: null,
  factorActionRequester: '',
  isRequestingEmail: false,
  isRequestingFactorAction: false,
  isRequestingSessionSave: false,
  isRequestingPdf: false,
  matchingSessionIDs: [],
  pages: [
    {pathname: '/session/selected_decision', component: SelectedDecisionContainer, data: {excludeProgressBar: true}},
    {pathname: '/session/educational_materials', component: EducationalMaterialsContainer, data: {excludeProgressBar: true}},
    {pathname: '/session/enter_factors_option_one', component: EnterFactorsContainer, data: {factorsOption: '1'} },
    {pathname: '/session/enter_factors_option_two', component: EnterFactorsContainer, data: {factorsOption: '2'} },
    {pathname: '/session/top_3_factors', component: TopThreeFactorsContainer, data: {excludeProgressBar: false}},
    {pathname: '/session/factor_effect', component: FactorEffectContainer},
    {pathname: '/session/order_of_importance', component: OrderOfImportanceContainer, data: {excludeProgressBar: false}},
    {pathname: '/session/comparing_importance', component: ComparingImportanceContainer, data: {excludeProgressBar: false}},
    {pathname: '/session/report', component: ReportContainer},
    {pathname: '/session/demographics', component: DemographicsContainer},
    {pathname: '/session/comments', component: CommentsContainer}
  ],
  pdfURL: '',
  sessionId: null,
  sessionType: '', // 'new' or 'recalled'
  updatedFactor: '',
  unmodifiedApiResponse: {},
  translations: {
    loading: false,
    original: null,
    others: null,
    error: null,
    setTransLoading: false,
    needsTranslations: false
  }
}

const counselingSession = (state = initialState, action) => {

  switch(action.type) {
    case actions.ADD_COUNSELOR_COMMENTS:
      return dotProp.set(state, 'counselingSessionData.counselorComments', action.payload.comments)

    case actions.ADD_FACTOR:
      const whichOptionAdd = action.payload.whichOption
      return dotProp.set(state, `counselingSessionData.${whichOptionAdd}`, factorsOption => [...factorsOption, action.payload.factorString])

    case actions.ADD_PATIENT_DETAILS:
      return dotProp.set(state, `counselingSessionData.${action.payload.name}`, action.payload.value)

    case actions.AUTOPOPULATE_TOP_THREE_FACTORS:
      return dotProp.set(state, 'counselingSessionData.topThreeFactors', action.payload.factors)

    case actions.COUNSELING_SESSION_NAVIGATION:
      return state

    case actions.EDUCATIONAL_MATERIALS_REVIEWED:
      return dotProp.set(state, 'counselingSessionData.informationReviewed', action.payload.isReviewed)

    case actions.COUNSELING_SESSION_SAVE_FAILURE:
      state = dotProp.set(state, 'errors.sessionSave', true)
      state = dotProp.set(state, 'isRequestingSessionSave', false)
      return state

    case actions.COUNSELING_SESSION_SAVE_REQUESTED:
      state = dotProp.set(state, 'indicatorMessage', 'Saving')
      return dotProp.set(state, 'isRequestingSessionSave', true)

    case actions.COUNSELING_SESSION_SAVE_SUCCESS:
      return dotProp.set(state, 'isRequestingSessionSave', false)

    case actions.SEND_EMAIL_REQUEST:
      state = dotProp.set(state, 'indicatorMessage', 'Sending Email')
      return dotProp.set(state, 'isRequestingEmail', true)

    case actions.SEND_EMAIL_SUCCESS:
      return dotProp.set(state, 'isRequestingEmail', false)
    case actions.SEND_EMAIL_FAIL:
      return dotProp.set(state, 'isRequestingEmail', false)

    case actions.FACTOR_COMPARE_UPDATE_WEIGHT: {
      const {index, weight} = action.payload
      return dotProp.set(
        state,
        `counselingSessionData.topThreeFactorsWithCompareWeights.${index}.weight`,
        weight
      )
    }

    case actions.FACTOR_EFFECT_UPDATE_WEIGHT: {
      const {index, weight} = action.payload
      return dotProp.set(
        state,
        `counselingSessionData.topThreeFactorsWithAffectWeights.${index}.weight`,
        weight
      )
    }

    case actions.GET_DECISION_RESULT_SUCCESS:
      return dotProp.set(state, 'counselingSessionData.finalWeight', action.payload.response.score)

    case actions.MARK_SESSION_COMPLETE:
      return dotProp.set(state, 'counselingSessionData.isComplete', true)

    case actions.MATCHING_SESSION_IDS_REQUEST_SUCCESS:
      return dotProp.set(state, 'matchingSessionIDs', action.payload.response)

    case actions.SET_ORDER_FACTOR_IMPORTANCE:
      return dotProp.set(state, 'counselingSessionData.topThreeFactorsWithCompareWeights', action.payload)

    case actions.REORDER_FACTOR_IMPORTANCE:
      const { reorder, result } = action.payload
      // dropped outside the list
      if (!result.destination) {
        return state
      }

      // Update Weight compare and affect
      const reorderTopThreeFactors = state.counselingSessionData.topThreeFactors.slice()
      const reorderWeightCompareFactors = state.counselingSessionData.topThreeFactorsWithCompareWeights
      const reorderWeightAffectFactors = state.counselingSessionData.topThreeFactorsWithAffectWeights

      let tempReorderWeightCompareFactors = reorder(
        reorderWeightCompareFactors,
        result.source.index,
        result.destination.index
      )

      // Reset compare weights
      tempReorderWeightCompareFactors = tempReorderWeightCompareFactors.map(ele => {
        if (ele) {
          ele.weight = 0
        }
        return ele
      })

      const tempReorderWeightAffectFactors = reorder(
        reorderWeightAffectFactors,
        result.source.index,
        result.destination.index
      )

      const tempReorderTopThreeFactors = reorder(
        reorderTopThreeFactors,
        result.source.index,
        result.destination.index
      )

      state = dotProp.set(state, 'counselingSessionData.hasBeenRanked', 1)
      state = dotProp.set(state, 'counselingSessionData.topThreeFactorsWithAffectWeights', tempReorderWeightAffectFactors)
      state = dotProp.set(state, 'counselingSessionData.topThreeFactorsWithCompareWeights', tempReorderWeightCompareFactors)
      return dotProp.set(state, 'counselingSessionData.topThreeFactors', tempReorderTopThreeFactors)

    case actions.POPULATE_COUNSELOR_NAME:
      /*
        Currently this value is populated via LDAP credentials. However, when recalling
        session this value is populated from DCP Admin.. These values are not necessarily
        identical. Future work on the api should provide a single source of truth.
      */
      const {user: {givenName: counselorFirstName, surname: counselorLastName}} = action.payload
      state = dotProp.set(state, 'counselingSessionData.counselorFirstName', counselorFirstName)
      return dotProp.set(state, 'counselingSessionData.counselorLastName', counselorLastName)

    case actions.SET_COMPARE_WEIGHTED_FACTORS:
      return dotProp.set(
        state,
        'counselingSessionData.topThreeFactorsWithCompareWeights',
        action.payload.compareWeightedFactors
      )

    case actions.SET_EFFECT_WEIGHTED_FACTORS:
      return dotProp.set(
        state,
        'counselingSessionData.topThreeFactorsWithAffectWeights',
        action.payload.effectWeightedFactors
      )

    case actions.REMOVE_FACTOR:
      const {whichOption, factorIndex} = action.payload
      return dotProp.delete(state, `counselingSessionData.${whichOption}.${factorIndex}`)

    case actions.RESET_AFFECT_COMPARE_ARRAYS:
      state = dotProp.set(state, 'counselingSessionData.topThreeFactorsWithAffectWeights', [])
      state = dotProp.set(state, 'counselingSessionData.topThreeFactorsWithCompareWeights', [])
      return state

    case actions.RESET_COMPARE_ARRAY:
      return dotProp.set(state, 'counselingSessionData.topThreeFactorsWithCompareWeights', [])

    case actions.RESET_FINAL_WEIGHT:
      state = dotProp.set(state, 'counselingSessionData.finalWeight', 0.0)
      return state

    case actions.RESET_TOP3_AFFECT_COMPARE_ARRAYS:
      state = dotProp.set(state, 'counselingSessionData.topThreeFactors', [])
      state = dotProp.set(state, 'counselingSessionData.topThreeFactorsWithAffectWeights', [])
      state = dotProp.set(state, 'counselingSessionData.topThreeFactorsWithCompareWeights', [])
      return state

    case actions.SESSION_EXIT:
      // reset counseling session
      // @todo will need to save counselor most likely
      return initialState

    case actions.SESSION_INITIALIZE_SUCCESS:
      state = dotProp.set(state, 'bypassSessionLoadFromCookie', true)
      state = dotProp.set(state, 'counselingSessionData.educationMaterials', action.payload.educationMaterials)
      state = dotProp.set(state, 'counselingSessionData.caseId', action.payload.caseId)
      state = dotProp.set(state, 'sessionId', action.payload.sessionId)
      state = dotProp.set(state, 'counselingSessionData.decisionName', action.payload.decisionName)
      state = dotProp.set(state, 'counselingSessionData.options', action.payload.options)
      state = dotProp.set(state, 'counselingSessionData.optionsResults', action.payload.optionsResults)
      state = dotProp.set(state, 'counselingSessionData.primaryLanguageCode', action.payload.lang)
      state = dotProp.set(state, 'sessionType', 'New Session')
      return state

    case actions.SESSION_RECALL_SUCCESS:
      const { apiDataReadyForStore, sessionId, unmodifiedApiResponse } = action.payload
      state = dotProp.set(state, 'bypassSessionLoadFromCookie', true)
      state = dotProp.set(state, 'counselingSessionData', apiDataReadyForStore)
      state = dotProp.set(state, 'sessionId', sessionId)
      state = dotProp.set(state, 'unmodifiedApiResponse', unmodifiedApiResponse)
      state = dotProp.set(state, 'sessionType', 'Recalled Session')
      return state

    case actions.SESSION_REPORT_PDF_GENERATION_FAILURE:
      state = dotProp.set(state, 'pdfURL', '')
      state = dotProp.set(state, 'errors.pdfGeneration', true)
      state = dotProp.set(state, 'isRequestingPdf', false)
      return state

    case actions.SESSION_REPORT_PDF_GENERATION_REQUESTED:
      return dotProp.set(state, 'isRequestingPdf', true)

    case actions.SESSION_REPORT_PDF_GENERATION_SUCCESS:
      const {pdfURL} = action.payload
      state = dotProp.set(state, 'errors.pdfGeneration', false)
      state = dotProp.set(state, 'pdfURL', pdfURL)
      state = dotProp.set(state, 'isRequestingPdf', false)
      return state

    case actions.SKIP_ENTERING_FACTORS_FOR_OPTION:
      const whichOptionSkip = action.payload.whichOption
      return dotProp.set(state, `counselingSessionData.${whichOptionSkip}`, [])

    case actions.TOP_THREE_FACTORS_TOGGLE:
      const { arrayAction, factorString } = action.payload
      const { factorsOption1, factorsOption2, topThreeFactors } = state.counselingSessionData
      const index = state.counselingSessionData.topThreeFactors.indexOf(factorString)
      if(arrayAction === 'remove') {
        return dotProp.delete(state, `counselingSessionData.topThreeFactors.${index}`)
      }
      // Add new factor but keep top 3 factors in order they were typed
      const topThreeFactorsStore = topThreeFactors
        .concat(factorString)
        .reduce((acc, ele) => {
          let index = factorsOption1.indexOf(ele)
          if (index === -1) { // Factors Options 2
            index = factorsOption2.indexOf(ele)
            acc[index + 2] = ele
          } else { //Factors Options 1
            acc[index] = ele
          }
          return acc
        }, {})
      const updatedTopThreeFactors = Object.keys(topThreeFactorsStore)
        // Some browsers auto sort object keys but don't rely on that
        .sort((a,b) => topThreeFactorsStore[a]-topThreeFactorsStore[b])
        .map(ele => topThreeFactorsStore[ele])
      return dotProp.set(state, 'counselingSessionData.topThreeFactors', updatedTopThreeFactors)

    case actions.UPDATE_DEMOGRAPHICS:
      const { dateOfBirth, firstName, lastName, medicalRecordNumber, gender, race, ethnicity, maritalStatus, education } = action.payload.demographics
      state = dotProp.set(state, 'counselingSessionData.dateOfBirth', dateOfBirth)
      state = dotProp.set(state, 'counselingSessionData.firstName', firstName)
      state = dotProp.set(state, 'counselingSessionData.lastName', lastName)
      state = dotProp.set(state, 'counselingSessionData.medicalRecordNumber', medicalRecordNumber)
      state = dotProp.set(state, 'counselingSessionData.gender', gender)
      state = dotProp.set(state, 'counselingSessionData.race', race)
      state = dotProp.set(state, 'counselingSessionData.ethnicity', ethnicity)
      state = dotProp.set(state, 'counselingSessionData.maritalStatus', maritalStatus)
      state = dotProp.set(state, 'counselingSessionData.education', education)
      return state

    case actions.UPDATE_USER_COMMENTS:
      const {comments} = action.payload
      state = dotProp.set(state, 'counselingSessionData.userComments', comments)
      return state

    case actions.UPDATE_FACTOR:
      const whichOptionUpdate = action.payload
      const updatedFactor = state.updatedFactor
      const updateFactorIndex = state.editingFactorIndex
      state = dotProp.set(state, `counselingSessionData.${whichOptionUpdate}.${updateFactorIndex}`, updatedFactor)
      state = dotProp.set(state, 'editingFactorIndex', null)
      return dotProp.set(state, 'updatedFactor', '')

    case actions.BEGIN_EDITING_FACTOR: {
      const {index, updatedFactor} = action.payload

      state = dotProp.set(state, 'editingFactorIndex', index)
      return dotProp.set(state, 'updatedFactor', updatedFactor)
    }
    case actions.EDITED_FACTOR_CHANGED:
      return dotProp.set(state, 'updatedFactor', action.payload)

    case actions.END_EDITING_FACTOR:
      state = dotProp.set(state, 'editingFactorIndex', null)
      return dotProp.set(state, 'updatedFactor', '')

    case actions.REQUEST_FACTOR_ACTION:
      state = dotProp.set(state, 'isRequestingFactorAction', true)
      return dotProp.set(state, 'factorActionRequester', action.payload)

    case actions.DISMISS_FACTOR_ACTION_MODAL:
      state = dotProp.set(state, 'factorActionRequester', '')
      return dotProp.set(state, 'isRequestingFactorAction', false)

    case actions.SESSION_TRANSLATION_INIT:
      return dotProp.set(state, 'translations.loading', true)

    case actions.SESSION_TRANSLATION_SUCCESS:
      const { original, others } = action.payload
      state = dotProp.set(state, 'translations.loading', false)
      state = dotProp.set(state, 'translations.original', original)
      state = dotProp.set(state, 'translations.others', others)
      return state

    case actions.SESSION_TRANSLATION_FAIL:
        state = dotProp.set(state, 'translations.loading', false)
        return dotProp.set(state, 'translations.error', action.payload)
    case actions.SESSION_SET_TRANSLATION_LOADING:
      return dotProp.set(state, 'translations.setTransLoading', action.payload)
    case actions.SESSION_ADD_TRANSLATION:
      const merged = state.counselingSessionData.translations.concat(action.payload)
      return dotProp.set(state, 'counselingSessionData.translations', merged)
    case actions.SESSION_SET_TRANSLATION_SUCCESS:
      return dotProp.set(state, `translations.others.${action.payload.langCode}`, action.payload.translations)

    case actions.RESET_HAS_BEEN_RANKED:
      return dotProp.set(state, 'counselingSessionData.hasBeenRanked', 0)


    default:
      return state
  }
}

export default counselingSession


// WEBPACK FOOTER //
// ./src/reducers/counseling-session.js